import * as React from "react"
import { useState } from "react"
import { Autocomplete, Box, Checkbox, Chip, CircularProgress, TextField, createFilterOptions } from "@mui/material"
import { FlagsIconSeeker } from "@ui-seeker/core"

import VirtualizedListbox from "./VirtualizedListbox"

const SELECT_ALL_OPTION = { value: '__all__', label: 'Select All', countryCode: null }
const baseFilter = createFilterOptions()

/**
 * Multi-select for site catalogs: per-option checkbox + country flag, and a
 * pinned "Select All" row that toggles the options matching the typed text.
 * It only shows up once something has been typed — selecting the whole catalog
 * returns exactly what selecting nothing returns, just far slower. The dropdown
 * stays open while selecting and the typed text survives each selection.
 *
 * Expects the FULL catalog in `options` (upfront load) — filtering is
 * client-side over everything, and the list is virtualized so only the visible
 * rows reach the DOM. `value` and `onChange` work with option objects
 * [{value, label, countryCode}].
 */
export default function SitesMultiSelect({ options, value, onChange, loading = false,
                                           label = "Sites", placeholder = "Search Sites...",
                                           error = false, ...props }) {
    const [inputValue, setInputValue] = useState('')

    const getFiltered = (input) =>
        baseFilter(options, { inputValue: input, getOptionLabel: (o) => o.label })

    return <Autocomplete
        multiple
        size="small"
        // Opt-in: the caller owns the fetch here too, so it is the only one that knows.
        // Defaults to false, so the pages that do not pass it behave exactly as before.
        loading={loading}
        loadingText="Loading sites…"
        disableCloseOnSelect
        disableListWrap
        ListboxComponent={VirtualizedListbox}
        options={[SELECT_ALL_OPTION, ...options]}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        value={value}
        inputValue={inputValue}
        onInputChange={(_, newInput, reason) => {
            // 'reset' fires after each selection; keep the typed text so the filter survives
            if (reason !== 'reset') setInputValue(newInput)
        }}
        onBlur={() => setInputValue('')}
        filterOptions={(opts, state) => {
            const filtered = baseFilter(opts.filter(o => o.value !== '__all__'), state)
            // "Select All" only appears once the text narrows the catalog. With an
            // empty input it would select every site, which is the same query as
            // selecting none — same result set, 6x slower (measured on puig: 11s
            // with no filter vs 66s with all 883 ids in the IN clause).
            const canSelectAll = state.inputValue.trim().length > 0
            return canSelectAll && filtered.length > 0 ? [SELECT_ALL_OPTION, ...filtered] : filtered
        }}
        onChange={(_, newValue) => {
            if (newValue.some(v => v.value === '__all__')) {
                if (!inputValue.trim()) return   // no catalog-wide select, see filterOptions
                const filtered = getFiltered(inputValue)
                const selectedValues = new Set(value.map(s => s.value))
                const allFilteredSelected = filtered.length > 0 && filtered.every(o => selectedValues.has(o.value))
                if (allFilteredSelected) {
                    const filteredValues = new Set(filtered.map(o => o.value))
                    onChange(value.filter(s => !filteredValues.has(s.value)))
                } else {
                    onChange([...value, ...filtered.filter(o => !selectedValues.has(o.value))])
                }
            } else {
                onChange(newValue)
            }
        }}
        renderOption={(optionProps, option, { selected }) => {
            if (option.value === '__all__') {
                return (
                    <li {...optionProps} key="__all__">
                        <strong>Select All</strong>
                    </li>
                )
            }
            return (
                <li {...optionProps} key={option.value}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                    {option.countryCode && (
                        <Box sx={{ mr: 0.75, display: 'flex', alignItems: 'center' }}>
                            <FlagsIconSeeker countryName={option.countryCode} />
                        </Box>
                    )}
                    <span>{option.label}</span>
                </li>
            )
        }}
        renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
                <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.label}
                    size="small"
                />
            ))
        }
        renderInput={(params) => (
            <TextField
                {...params}
                label={label}
                placeholder={value.length === 0 ? placeholder : ''}
                size="small"
                // Forwarded explicitly: the {...props} below land on the
                // Autocomplete, so a caller that marks this required had no way
                // to reach the field that actually paints the error.
                error={error}
                InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                        <>
                            {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                            {params.InputProps.endAdornment}
                        </>
                    ),
                }}
            />
        )}
        {...props}
    />
}
